/* eslint-disable @typescript-eslint/no-explicit-any */
import type { N8NServiceSchema } from "@/types/entities/n8n";
import { z } from "zod";
import {
  createDateTimeRangeSchema,
  getDateTimeRangeDefaultValues,
} from "./date-time-range";
import { createDateRangeSchema, getDateRangeDefaultValues } from "./date-range";
import { createDateTimeSchema, getDateTimeDefaultValues } from "./date-time";
import type { NodeData } from "@/workflow_api/types/workflow";
import dayjs from "dayjs";

// given a n8n service schema, builds a simplified schema representation using zod
export const N8NSchemaBuilder = (schema: N8NServiceSchema["sections"]) => {
  const allInputs = schema.reduce(
    (acc, section) => {
      return { ...acc, ...section.inputs };
    },
    {} as Record<string, { type: string; required: boolean }>,
  );

  const zodSchemaShape: Record<string, any> = {};

  Object.entries(allInputs).forEach(([key, value]) => {
    let zodType;
    switch (value.type) {
      case "date-time-range": {
        // if type if input, validate required for start and end and value can be empty, if type is variable, just validate value
        zodType = createDateTimeRangeSchema(value.required);
        break;
      }
      case "date-range": {
        zodType = createDateRangeSchema(value.required);

        break;
      }
      case "date-time": {
        zodType = createDateTimeSchema(value.required);
        break;
      }
      case "email": {
        zodType = z
          .object({
            type: z.enum(["input", "variable"]),
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (data.type === "input") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo es obligatorio",
                    path: ["value"],
                  });
                }
              }
              if (data.type === "variable") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "El valor de la variable es obligatorio",
                    path: ["value"],
                  });
                }
              }
            }
          });
        break;
      }
      case "text": {
        zodType = z
          .object({
            type: z.enum(["input", "variable"]),
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (data.type === "input") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo es obligatorio",
                    path: ["value"],
                  });
                }
              }
              if (data.type === "variable") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "El valor de la variable es obligatorio",
                    path: ["value"],
                  });
                }
              }
            }
          });
        break;
      }
      case "number":
        zodType = z
          .object({
            type: z.enum(["input", "variable"]),
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (data.type === "input") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo es obligatorio",
                    path: ["value"],
                  });
                } else if (isNaN(Number(data.value))) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo debe ser un número válido",
                    path: ["value"],
                  });
                }
              }
              if (data.type === "variable") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "El valor de la variable es obligatorio",
                    path: ["value"],
                  });
                }
              }
            }
          });
        break;
      case "boolean":
        zodType = z
          .object({
            type: z.enum(["input", "variable"]),
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (data.type === "input") {
                if (data.value !== "true" && data.value !== "false") {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo debe ser verdadero o falso",
                    path: ["value"],
                  });
                }
              }
              if (data.type === "variable") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "El valor de la variable es obligatorio",
                    path: ["value"],
                  });
                }
              }
            }
          });
        break;
      case "date":
        zodType = z
          .object({
            type: z.enum(["input", "variable"]),
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (data.type === "input") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo es obligatorio",
                    path: ["value"],
                  });
                } else if (!dayjs(data.value, "DD/MM/YYYY").isValid()) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo debe ser una fecha válida",
                    path: ["value"],
                  });
                }
              }
              if (data.type === "variable") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "El valor de la variable es obligatorio",
                    path: ["value"],
                  });
                }
              }
            }
          });
        break;
      case "array":
      case "select": {
        zodType = z
          .object({
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (!data.value || data.value.trim().length === 0) {
                ctx.addIssue({
                  code: "custom",
                  message: "Este campo es obligatorio",
                  path: ["value"],
                });
              }
            }
          });
        break;
      }
      case "file": {
        zodType = z
          .object({
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (!data.value || data.value.trim().length === 0) {
                ctx.addIssue({
                  code: "custom",
                  message: "Este campo es obligatorio",
                  path: ["value"],
                });
              }
            }
          });
        break;
      }
      case "id": {
        zodType = z
          .object({
            type: z.enum(["input", "variable"]),
            value: z.string().optional(),
            node_id: z.string().optional(),
            value_type: z.enum(["field", "object", ""]).optional(),
          })
          .superRefine((data, ctx) => {
            if (value.required) {
              if (data.type === "input") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "Este campo es obligatorio",
                    path: ["value"],
                  });
                }
              }
              if (data.type === "variable") {
                if (!data.value || data.value.trim().length === 0) {
                  ctx.addIssue({
                    code: "custom",
                    message: "El valor de la variable es obligatorio",
                    path: ["value"],
                  });
                }
              }
            }
          });
        break;
      }
      default: {
        zodType = z.string();
        if (value.required) {
          zodType = zodType.min(1, "Este campo es obligatorio");
        } else {
          zodType = zodType.optional();
        }
        break;
      }
    }
    zodSchemaShape[key] = zodType;
  });
  return z.object(zodSchemaShape);
};

export const N8NDefaultValuesBuilder = (
  schema: N8NServiceSchema["sections"],
  data: NodeData["inputs"],
) => {
  const allInputs = schema.reduce(
    (acc, section) => {
      return { ...acc, ...section.inputs };
    },
    {} as Record<string, { type: string; required: boolean }>,
  );

  const defaultValues: Record<string, any> = {};

  Object.entries(allInputs).forEach(([key, value]) => {
    switch (value.type) {
      case "date-time-range": {
        const input = data.find((i) => i.name === key);
        const isVar = input?.source_connections;
        if ((!isVar || isVar.length === 0) && !input?.value) {
          defaultValues[key] = getDateTimeRangeDefaultValues();
          break;
        }

        if (input?.value && !input.global_type) {
          const [start, end] = (input.value as string).split(",");
          const [startDate, ...startTime] = start.split(" ");
          const [endDate, ...endTime] = end.split(" ");

          defaultValues[key] = {
            ...getDateTimeDefaultValues(),
            type: "input",
            start: startDate || "",
            start_time: startTime.join(" ") || "",
            end: endDate || "",
            end_time: endTime.join(" ") || "",
          };
          break;
        }

        if ((isVar && isVar.length > 0) || input?.global_type) {
          defaultValues[key] = {
            ...getDateTimeRangeDefaultValues(),
            ...getValueFromInput(input!),
          };
        }
        break;
      }
      case "date-range": {
        const input = data.find((i) => i.name === key);
        const isVar = input?.source_connections;
        if ((!isVar || isVar.length === 0) && !input?.global_type) {
          const [start, end] = ((input?.value || "") as string).split(",");
          defaultValues[key] = {
            ...getDateRangeDefaultValues(),
            start: start || "",
            end: end || "",
          };
        }
        if ((isVar && isVar.length > 0) || input?.global_type) {
          defaultValues[key] = {
            ...getDateRangeDefaultValues(),
            ...getValueFromInput(input!),
          };
        }
        break;
      }
      case "date-time": {
        const input = data.find((i) => i.name === key);
        const isVar = input?.source_connections;
        if ((!isVar || isVar.length === 0) && !input?.global_type) {
          const [date, time] = ((input?.value || "") as string).split(" ");
          defaultValues[key] = {
            ...getDateTimeDefaultValues(),
            date: date || "",
            time: time || "",
          };
          break;
        }
        if ((isVar && isVar.length > 0) || input?.global_type) {
          defaultValues[key] = {
            ...getDateTimeDefaultValues(),
            ...getValueFromInput(input!),
          };
        }
        break;
      }
      case "id":
      case "number":
      case "email":
      case "text":
      case "date": {
        const input = data.find((i) => i.name === key);
        const isVar = input?.source_connections;
        if ((!isVar || isVar.length === 0) && !input?.global_type) {
          defaultValues[key] = {
            type: "input",
            value: input?.value || "",
            node_id: "",
            value_type: "",
          };
          break;
        }
        defaultValues[key] = {
          ...getValueFromInput(input!),
        };

        break;
      }
      case "boolean": {
        const input = data.find((i) => i.name === key);
        const isVar = input?.source_connections;
        if ((!isVar || isVar.length === 0) && !input?.global_type) {
          defaultValues[key] = {
            type: "input",
            value: (input?.value || "false").toString(),
            node_id: "",
            value_type: "",
          };
          break;
        }
        defaultValues[key] = {
          ...getValueFromInput(input!),
        };
        break;
      }
      case "file":
      case "array":
      case "select": {
        const input = data.find((i) => i.name === key);

        defaultValues[key] = {
          ...getValueFromInput(input!),
        };
        break;
      }
      default:
        defaultValues[key] = "";
        break;
    }
  });

  return defaultValues;
};

const getValueFromInput = (input: NodeData["inputs"][number]) => {
  if (input?.value && input?.global_type) {
    if (
      input?.global_type === "APPLICANT" ||
      input?.global_type === "CURRENT_TIME"
    ) {
      return {
        type: "variable",
        value: input?.value,
        node_id: "global-variables",
      };
    }
  }

  if (input?.value !== null) {
    return {
      type: "input",
      value: input?.value,
    };
  }

  const sourceConnection = input.source_connections?.[0];
  const output_name = sourceConnection?.output_name;
  const field_id = sourceConnection?.field_id;
  const source_node_id = sourceConnection?.source_node_id;

  if (output_name && field_id) {
    const formFormatId = `${field_id}-${output_name}`;

    return {
      type: "variable",
      value: formFormatId,
      node_id: source_node_id,
      field_id: field_id,
      output_name: output_name,
    };
  }

  if (output_name) {
    return {
      type: "variable",
      value: `${source_node_id}-${output_name}`,
      node_id: source_node_id,
      output_name: output_name,
    };
  }
  if (field_id) {
    return {
      type: "variable",
      value: field_id,
      node_id: source_node_id,
    };
  }
  if (source_node_id) {
    return {
      type: "variable",
      value: source_node_id,
      node_id: source_node_id,
    };
  }
  return {
    type: "input",
    value: "",
    node_id: "",
  };
};
